import { useEffect } from 'react';
import { useUI } from './store';

/**
 * Global keyboard shortcuts. Keep the layer small — every shortcut is a
 * single-keystroke verb, never a chord. Inputs and textareas are exempt
 * (we never want `n` to swallow a real letter she's typing).
 *
 *   Esc   — close any open sheet (priority order: help → history → compose
 *           → selected pin)
 *   n     — open place compose
 *   m     — leave a note
 *   ?     — toggle the help overlay (Shift+/, also bare ?)
 */
export function useKeyboardShortcuts() {
  const selectPin = useUI((s) => s.selectPin);
  const startCompose = useUI((s) => s.startCompose);
  const endCompose = useUI((s) => s.endCompose);
  const composing = useUI((s) => s.composing);
  const selectedPinId = useUI((s) => s.selectedPinId);
  const historyOpen = useUI((s) => s.historyOpen);
  const closeHistory = useUI((s) => s.closeHistory);
  const helpOpen = useUI((s) => s.helpOpen);
  const openHelp = useUI((s) => s.openHelp);
  const closeHelp = useUI((s) => s.closeHelp);

  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    }

    function handler(e: KeyboardEvent) {
      // Modifier-bearing shortcuts intentionally fall through; keep the
      // surface small so we don't conflict with browser defaults.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Esc always works, even mid-typing — that's the universal escape valve.
      if (e.key === 'Escape') {
        if (helpOpen) { closeHelp(); e.preventDefault(); return; }
        if (historyOpen) { closeHistory(); e.preventDefault(); return; }
        if (composing) { endCompose(); e.preventDefault(); return; }
        if (selectedPinId) { selectPin(null); e.preventDefault(); return; }
        return;
      }

      if (isTypingTarget(e.target)) return;

      // ? — toggle help. Browsers send '?' on Shift+/, so check both forms.
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        helpOpen ? closeHelp() : openHelp();
        e.preventDefault();
        return;
      }

      // Don't open new sheets if anything is already open.
      const anySheetOpen = !!composing || !!selectedPinId || historyOpen || helpOpen;
      if (anySheetOpen) return;

      if (e.key === 'n' || e.key === 'N') {
        startCompose({ kind: 'place' });
        e.preventDefault();
      } else if (e.key === 'm' || e.key === 'M') {
        startCompose({ kind: 'note' });
        e.preventDefault();
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    composing, selectedPinId, historyOpen, helpOpen,
    selectPin, startCompose, endCompose, closeHistory, openHelp, closeHelp,
  ]);
}
